(function () {
  'use strict';

  if (window.milauraRecommendationsReady) return;
  window.milauraRecommendationsReady = true;

  const HISTORY_KEY = 'milauraRecentlyViewed';
  const HISTORY_LIMIT = 6;
  const RECOMMENDATION_FRAGMENT = 'milaura-recommendation-fragment';
  const PRODUCT_FRAGMENT = 'milaura-product-fragment';
  const RECENT_FRAGMENT = 'milaura-recent-fragment';
  const PDP_SCORE_RECENT_TIEBREAKER = 8;
  const PDP_SCORE_API_ORDER_MAX = 10;
  const PDP_SCORE_BEAD_SIZE_MAX = 12;
  const PDP_SCORE_BEAD_SIZE_STEP = 2;
  const PDP_SCORE_PRICE_PENALTY = -40;
  const PDP_CANDIDATE_LIMIT = 3;
  const PDP_CATALOGUE_SCAN_LIMIT = 250;
  const DIAGNOSTIC_SEARCH_LIMIT = 10;
  const DIAGNOSTIC_CARD_CANDIDATE_LIMIT = 6;
  const DIAGNOSTIC_FALLBACK_QUERY = 'bijoux pierres naturelles';
  const DIAGNOSTIC_INTENT_HANDLES = Object.freeze({
    apaisement: Object.freeze(['calme', 'sommeil', 'douceur']),
    protection: Object.freeze(['protection', 'ancrage']),
    serenite: Object.freeze(['calme', 'sommeil', 'intuition']),
    amour: Object.freeze(['amour', 'douceur']),
    chance: Object.freeze(['confiance', 'energie']),
  });
  const DIAGNOSTIC_PRODUCT_HOOKS = Object.freeze({
    apaisement: Object.freeze([
      'Pour relâcher la pression.',
      'Pour arrêter de vous en demander trop.',
      'Pour vous accorder une vraie pause.',
    ]),
    protection: Object.freeze([
      'Pour poser une limite.',
      'Pour prendre de la distance.',
      'Pour préserver votre énergie.',
    ]),
    serenite: Object.freeze([
      'Pour faire de la place dans votre tête.',
      'Pour retrouver une priorité claire.',
      'Pour fermer la journée.',
    ]),
    amour: Object.freeze([
      'Pour vous remettre au centre.',
      'Pour recevoir autant que vous donnez.',
      'Pour garder un moment pour vous.',
    ]),
    chance: Object.freeze([
      'Pour faire le premier pas.',
      'Pour vous donner une chance.',
      'Pour avancer sans tout maîtriser.',
    ]),
  });
  const DIAGNOSTIC_TYPE_PRIORITY = Object.freeze({
    collier: 120,
    bracelet: 115,
    'boucles-oreilles': 110,
    pendentif: 105,
    bague: 100,
    chapelet: 90,
    bougie: 70,
  });
  const diagnosticSearchCache = new Map();
  const TYPE_COMPATIBILITY = Object.freeze({
    bracelet: Object.freeze({ 'boucles-oreilles': 30, collier: 25, bague: 20, pendentif: 12 }),
    collier: Object.freeze({ 'boucles-oreilles': 30, bracelet: 25, bague: 20 }),
    'boucles-oreilles': Object.freeze({ collier: 30, bracelet: 25, bague: 15 }),
    bague: Object.freeze({ collier: 25, 'boucles-oreilles': 25, bracelet: 20 }),
    pendentif: Object.freeze({ chaine: 45, bracelet: 10, 'boucles-oreilles': 10 }),
    chaine: Object.freeze({ pendentif: 45 }),
    chapelet: Object.freeze({ bracelet: 12, collier: 12 }),
  });

  function formatParurePrice(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format((Number(cents) || 0) / 100);
  }

  function storefrontRoot() {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  }

  function parseList(value) {
    return String(value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  function toHandle(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function productTagValues(product, namespace) {
    const prefix = `${namespace}:`;
    return (Array.isArray(product?.tags) ? product.tags : [])
      .map((tag) => String(tag || '').trim())
      .filter((tag) => tag.toLowerCase().startsWith(prefix))
      .map((tag) => toHandle(tag.slice(prefix.length)))
      .filter(Boolean);
  }

  function diagnosticProductType(product) {
    return productTagValues(product, 'type')[0] || toHandle(product?.type || product?.product_type || '');
  }

  function diagnosticIntentHandles(diagnostic) {
    const supplied = Array.isArray(diagnostic?.intentionHandles)
      ? diagnostic.intentionHandles.map(toHandle).filter(Boolean)
      : [];
    if (supplied.length) return supplied;
    return Array.from(DIAGNOSTIC_INTENT_HANDLES[diagnostic?.profileId] || []);
  }

  function classifyDiagnosticProduct(product, diagnostic) {
    const targetStone = toHandle(diagnostic?.stoneHandle || diagnostic?.stone || '');
    const candidateStones = productTagValues(product, 'pierre');
    const targetIntentions = diagnosticIntentHandles(diagnostic);
    const candidateIntentions = productTagValues(product, 'intention');
    const productType = diagnosticProductType(product);
    const exactStone = Boolean(targetStone && candidateStones.includes(targetStone));
    const stoneFamily = Boolean(
      targetStone && candidateStones.some((candidateStone) => compatibleStone(targetStone, candidateStone))
    );
    const intentionMatch = targetIntentions.some((intention) => candidateIntentions.includes(intention));
    let signal = 'catalogue';
    let score = 100;

    if (exactStone) {
      signal = 'stone';
      score = 1200;
    } else if (stoneFamily) {
      signal = 'stone-family';
      score = 1000;
    } else if (intentionMatch) {
      signal = 'intention';
      score = 600;
    }

    if (candidateStones.length || candidateIntentions.length) score += 30;
    if ((product?.tags || []).some((tag) => toHandle(tag) === 'bijoux-pierres')) score += 20;
    score += DIAGNOSTIC_TYPE_PRIORITY[productType] || 20;

    return { score, signal, productType };
  }

  function rankDiagnosticProducts(products, diagnostic, excludedIds) {
    const excluded = new Set((excludedIds || []).map(String));
    const seen = new Set();
    return (Array.isArray(products) ? products : [])
      .map((product, index) => {
        if (!product?.id || !product?.handle || product.available === false || !product.image) return null;
        const productId = String(product.id);
        if (excluded.has(productId) || seen.has(productId)) return null;
        seen.add(productId);
        const classification = classifyDiagnosticProduct(product, diagnostic);
        return { product, index, ...classification };
      })
      .filter(Boolean)
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        if (left.index !== right.index) return left.index - right.index;
        return String(left.product.id).localeCompare(String(right.product.id));
      });
  }

  function rankDiagnosticMatches(products, diagnostic, excludedIds) {
    return rankDiagnosticProducts(products, diagnostic, excludedIds).filter(
      (candidate) => candidate.signal !== 'catalogue'
    );
  }

  async function fetchDiagnosticSearchProducts(query) {
    const normalizedQuery = String(query || '').trim();
    if (!normalizedQuery) return [];
    if (diagnosticSearchCache.has(normalizedQuery)) return diagnosticSearchCache.get(normalizedQuery);

    const promise = (async () => {
      const params = new URLSearchParams({ q: normalizedQuery });
      params.set('resources[type]', 'product');
      params.set('resources[limit]', String(DIAGNOSTIC_SEARCH_LIMIT));
      params.set('resources[options][unavailable_products]', 'hide');
      params.set('resources[options][fields]', 'tag,title,product_type');
      const response = await fetch(`${storefrontRoot()}search/suggest.json?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error(`diagnostic_search_${response.status}`);
      const payload = await response.json();
      return Array.isArray(payload?.resources?.results?.products) ? payload.resources.results.products : [];
    })();

    diagnosticSearchCache.set(normalizedQuery, promise);
    try {
      return await promise;
    } catch (error) {
      diagnosticSearchCache.delete(normalizedQuery);
      throw error;
    }
  }

  function hasEnoughDiagnosticVariety(ranked, limit) {
    if (ranked.length < limit) return false;
    return new Set(ranked.slice(0, Math.max(limit * 2, 4)).map((candidate) => candidate.productType)).size > 1;
  }

  async function resolveDiagnosticProductItems(diagnostic, excludedIds, limit) {
    const targetStone = String(diagnostic?.stoneHandle || diagnostic?.stone || '').replace(/-/g, ' ').trim();
    let products = targetStone
      ? await fetchDiagnosticSearchProducts(targetStone).catch(function () { return []; })
      : [];
    let ranked = rankDiagnosticMatches(products, diagnostic, excludedIds);

    if (!hasEnoughDiagnosticVariety(ranked, limit)) {
      const intentionResults = await Promise.all(
        diagnosticIntentHandles(diagnostic)
          .slice(0, 2)
          .map((intention) => fetchDiagnosticSearchProducts(intention).catch(function () { return []; }))
      );
      products = products.concat(...intentionResults);
      ranked = rankDiagnosticMatches(products, diagnostic, excludedIds);
    }

    if (ranked.length < limit) {
      const fallback = await fetchDiagnosticSearchProducts(DIAGNOSTIC_FALLBACK_QUERY).catch(function () { return []; });
      ranked = rankDiagnosticProducts(
        ranked.map((candidate) => candidate.product).concat(fallback),
        diagnostic,
        excludedIds
      );
    }

    return ranked.slice(0, Math.max(limit * 3, DIAGNOSTIC_CARD_CANDIDATE_LIMIT));
  }

  function diagnosticReason(signal, stone) {
    if (signal === 'stone' || signal === 'stone-family') return `Sélection en ${stone || 'votre pierre'}.`;
    if (signal === 'intention') return 'Sélectionnée pour accompagner votre intention.';
    return 'Une création MilAura disponible maintenant.';
  }

  function diagnosticProductReason(diagnostic, index, signal) {
    const hooks = DIAGNOSTIC_PRODUCT_HOOKS[diagnostic?.profileId] || [];
    return hooks[index] || diagnosticReason(signal, diagnostic?.stone);
  }

  function selectDiverseDiagnosticCards(records, limit) {
    const remaining = records.slice();
    const selected = [];
    const usedTypes = new Set();

    while (remaining.length && selected.length < limit) {
      let index = remaining.findIndex((record) => !usedTypes.has(record.productType));
      if (index < 0) index = 0;
      const [record] = remaining.splice(index, 1);
      selected.push(record);
      if (record.productType) usedTypes.add(record.productType);
    }

    return selected;
  }

  function uniqueByProductId(cards, excludedIds, limit) {
    const excluded = new Set(excludedIds.map(String));
    const seen = new Set();
    const unique = [];

    cards.forEach((card) => {
      const productId = String(card.dataset.productId || '');
      if (!productId || excluded.has(productId) || seen.has(productId)) return;
      if (card.dataset.productAvailable === 'false') return;
      if (card.dataset.hasGalleryImage === 'false') return;
      seen.add(productId);
      unique.push(card);
    });

    return unique.slice(0, limit);
  }

  function compatibleStone(sourceStone, candidateStone) {
    if (!sourceStone || !candidateStone) return false;
    if (sourceStone === candidateStone) return true;
    return ['jade', 'obsidienne'].some((parent) => {
      const sourceInFamily = sourceStone === parent || sourceStone.startsWith(`${parent}-`);
      const candidateInFamily = candidateStone === parent || candidateStone.startsWith(`${parent}-`);
      return sourceInFamily && candidateInFamily;
    });
  }

  function audienceCompatible(sourceAudience, candidateAudience, candidateType) {
    if (sourceAudience === 'homme') return candidateType !== 'boucles-oreilles' && candidateAudience !== 'femme';
    if (sourceAudience === 'femme') return candidateAudience !== 'homme';
    return true;
  }

  function beadSizeBonus(sourceSize, candidateSize) {
    const source = Number.parseFloat(String(sourceSize || '').replace(',', '.'));
    const candidate = Number.parseFloat(String(candidateSize || '').replace(',', '.'));
    if (!Number.isFinite(source) || !Number.isFinite(candidate)) return 0;
    return Math.max(0, PDP_SCORE_BEAD_SIZE_MAX - PDP_SCORE_BEAD_SIZE_STEP * Math.abs(source - candidate));
  }

  function pricePenalty(sourcePrice, candidatePrice) {
    const source = Number(sourcePrice);
    const candidate = Number(candidatePrice);
    if (!(source > 0) || !(candidate > 0)) return 0;
    const ratio = candidate / source;
    return ratio > 4 || ratio < 0.1 ? PDP_SCORE_PRICE_PENALTY : 0;
  }

  function classifyPdpCandidate(card, source) {
    const candidateStone = card.dataset.stoneHandle || '';
    const candidateType = card.dataset.productType || '';
    const candidateIntention = card.dataset.primaryIntention || '';
    const candidateFamily = card.dataset.productFamily || '';
    const candidateFinish = card.dataset.productFinish || 'inconnu';
    const candidateAudience = card.dataset.productAudience || '';
    const candidateParure = card.dataset.productParure || '';
    const origin = card.dataset.recommendationOrigin || '';
    const fallbackTier = card.dataset.fallbackTier || '';
    const stoneMatch = compatibleStone(source.stone, candidateStone);
    const typeWeight = Number(TYPE_COMPATIBILITY[source.type]?.[candidateType] || 0);
    const finishMatch =
      source.finish === 'inconnu' || candidateFinish === 'inconnu' || source.finish === candidateFinish;
    const intentionMatch = Boolean(source.intention && source.intention === candidateIntention);
    const familyMatch = Boolean(source.family && source.family === candidateFamily);

    if (source.family === 'bijou' && candidateFamily && candidateFamily !== 'bijou') {
      return { gate: 'excluded', score: 0 };
    }
    if (!audienceCompatible(source.audience, candidateAudience, candidateType)) {
      return { gate: 'excluded', score: 0 };
    }
    const chainPendant = isChainPendantPair(source.type, candidateType);
    if (chainPendant) {
      const priority = source.parure && source.parure === candidateParure ? 600 :
        origin === 'shopify-complementary' ? 520 : finishMatch ? 510 : 300;
      return { gate: 'pairing', score: priority + typeWeight };
    }
    if (source.parure && source.parure === candidateParure && candidateType !== source.type) {
      return { gate: 'exact', score: 600 + typeWeight };
    }
    if (origin === 'shopify-complementary') return { gate: 'close', score: 520 + typeWeight };
    if (stoneMatch && typeWeight > 0 && finishMatch) {
      return { gate: 'exact', score: 500 + typeWeight };
    }
    if (stoneMatch && typeWeight > 0) return { gate: 'close', score: 440 + typeWeight };
    if (stoneMatch) return { gate: 'close', score: 420 };
    if (intentionMatch) return { gate: 'close', score: 380 + typeWeight };
    if (familyMatch) return { gate: 'universe', score: 300 + typeWeight };
    if (fallbackTier === 'same-collection') return { gate: 'collection', score: 200 };
    return { gate: 'catalogue', score: 100 };
  }

  function isChainPendantPair(sourceType, candidateType) {
    return (sourceType === 'chaine' && candidateType === 'pendentif') ||
      (sourceType === 'pendentif' && candidateType === 'chaine');
  }

  function selectParureCandidates(ranked, source, limit) {
    const typeOrder = Object.entries(TYPE_COMPATIBILITY[source.type] || {})
      .sort((left, right) => right[1] - left[1])
      .map(([type]) => type);
    const selected = [];
    const usedTypes = new Set();
    const metalOf = (candidate) => candidate.card.dataset.productFinish || 'inconnu';
    let selectionMetal = source.finish && source.finish !== 'inconnu' ? source.finish : '';
    const sameMetal = (candidate) => Boolean(selectionMetal) && metalOf(candidate) === selectionMetal;
    const metalFits = (candidate) => !selectionMetal || ['inconnu', selectionMetal].includes(metalOf(candidate));
    const take = (candidate) => {
      selected.push(candidate);
      usedTypes.add(candidate.type);
      if (!selectionMetal && metalOf(candidate) !== 'inconnu') selectionMetal = metalOf(candidate);
    };
    const free = (candidate) => !selected.includes(candidate);

    const pickPerType = (qualifies) => {
      typeOrder.forEach((type) => {
        if (selected.length >= limit || usedTypes.has(type)) return;
        const matches = (candidate) => candidate.type === type && qualifies(candidate) && free(candidate);
        const best =
          ranked.find((candidate) => matches(candidate) && sameMetal(candidate)) ||
          ranked.find((candidate) => matches(candidate) && metalFits(candidate)) ||
          ranked.find(matches);
        if (best) take(best);
      });
    };

    pickPerType((candidate) => candidate.parure || candidate.pairing);
    pickPerType((candidate) => candidate.sameStone);
    ranked.forEach((candidate) => {
      if (selected.length >= limit || !free(candidate) || usedTypes.has(candidate.type)) return;
      take(candidate);
    });
    ranked.forEach((candidate) => {
      if (selected.length >= limit || !free(candidate)) return;
      take(candidate);
    });
    return selected;
  }

  function setPdpRecommendationReason(card, gate) {
    const reason = card.querySelector('.milaura-recommendation-card__reason');
    if (!reason) return;
    const reasons = {
      exact: 'Même pierre, une forme complémentaire.',
      close: 'Une pièce accordée à votre choix.',
      pairing: 'Vérifiez le passage de l’embout dans la bélière.',
      universe: 'Un produit de la même famille.',
      collection: 'Une pièce disponible de la même collection.',
      catalogue: 'Une sélection MilAura disponible maintenant.',
    };
    reason.textContent = reasons[gate] || reasons.catalogue;
  }

  function rankPdpCards(cards, source, recentIds, excludedIds, limit) {
    const eligible = uniqueByProductId(cards, excludedIds, Number.MAX_SAFE_INTEGER)
      .map((card, index) => {
        const classification = classifyPdpCandidate(card, source);
        const productId = String(card.dataset.productId || '');
        let score = classification.score;
        if (classification.gate === 'excluded') return null;
        if (card.dataset.isBestseller === 'true') score += 20;
        if (recentIds.has(productId)) score += PDP_SCORE_RECENT_TIEBREAKER;
        score += Math.max(0, PDP_SCORE_API_ORDER_MAX - index);
        score += beadSizeBonus(source.beadSize, card.dataset.productBeadSize);
        score += pricePenalty(source.price, card.dataset.productPrice);
        card.dataset.recommendationGate = classification.gate;
        card.dataset.recommendationSignal = classification.gate;
        card.dataset.recommendationScore = String(score);
        setPdpRecommendationReason(card, classification.gate);
        const pairing = isChainPendantPair(source.type, card.dataset.productType || '');
        const parure = !pairing &&
          (classification.gate === 'exact' || card.dataset.recommendationOrigin === 'shopify-complementary');
        const sameStone = compatibleStone(source.stone, card.dataset.stoneHandle || '');
        const complementary = Number(TYPE_COMPATIBILITY[source.type]?.[card.dataset.productType || ''] || 0) > 0;
        card.dataset.parureMember = String(!pairing && (parure || (sameStone && complementary)));
        return { card, productId, score, index, type: card.dataset.productType || '', parure, sameStone,
          pairing: classification.gate === 'pairing' };
      })
      .filter(Boolean);

    eligible.sort((left, right) => {
      if (right.score !== left.score) return right.score - left.score;
      if (left.index !== right.index) return left.index - right.index;
      return left.productId.localeCompare(right.productId);
    });
    return selectParureCandidates(eligible, source, limit).map((candidate) => candidate.card);
  }

  async function fetchCartProductIds() {
    const response = await fetch(`${storefrontRoot()}cart.js`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return [];
    const cart = await response.json();
    const seen = new Set();
    return (Array.isArray(cart.items) ? cart.items : [])
      .map((item) => String(item.product_id || ''))
      .filter((productId) => {
        if (!productId || seen.has(productId)) return false;
        seen.add(productId);
        return true;
      });
  }

  function publishAnalytics(name, payload) {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    try {
      const result = publish.call(window.Shopify.analytics, name, payload);
      if (result && typeof result.catch === 'function') result.catch(function () {});
    } catch (error) {
      // Analytics never blocks navigation or purchase.
    }
  }

  function prepareHistory(currentProduct) {
    if (window.milauraRecommendationHistoryPromise) return window.milauraRecommendationHistoryPromise;

    window.milauraRecommendationHistoryPromise = window.MilauraPreferenceStorage.getPreferenceState().then((privacy) => {
      if (!privacy.available) return [];
      if (!privacy.allowed) {
        try {
          window.localStorage.removeItem(HISTORY_KEY);
        } catch (error) {}
        return [];
      }

      let history = [];
      try {
        const parsed = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]');
        if (Array.isArray(parsed)) history = parsed;
      } catch (error) {
        history = [];
      }

      history = history.filter((item) => item && item.id && item.handle);

      if (currentProduct?.id && currentProduct?.handle) {
        const currentId = String(currentProduct.id);
        history = history.filter((item) => String(item.id) !== currentId && item.handle !== currentProduct.handle);
        history.unshift({ id: currentId, handle: currentProduct.handle });
        history = history.slice(0, HISTORY_LIMIT);
        try {
          window.localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
        } catch (error) {}
      }

      return history;
    });

    return window.milauraRecommendationHistoryPromise;
  }

  async function fetchRecommendationCards(productId, intent, limit) {
    const query = new URLSearchParams({
      product_id: productId,
      limit: String(Math.min(Math.max(limit, 1), 10)),
      section_id: RECOMMENDATION_FRAGMENT,
      intent,
    });
    const response = await fetch(`${storefrontRoot()}recommendations/products?${query.toString()}`, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) throw new Error(`recommendations_${response.status}`);

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const fragment = documentFragment.querySelector('[data-milaura-recommendation-fragment]');
    if (!fragment) return [];
    return Array.from(fragment.querySelectorAll('[data-milaura-recommendation-card]')).map((card) =>
      document.importNode(card, true)
    );
  }

  async function fetchRecentCard(handle) {
    const path = `${storefrontRoot()}products/${encodeURIComponent(handle)}?section_id=${RECENT_FRAGMENT}`;
    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return null;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = documentFragment.querySelector('[data-milaura-recent-fragment] [data-milaura-recommendation-card]');
    return card ? document.importNode(card, true) : null;
  }

  async function fetchProductCard(handle) {
    const path = `${storefrontRoot()}products/${encodeURIComponent(handle)}?section_id=${PRODUCT_FRAGMENT}`;
    const response = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) return null;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const card = documentFragment.querySelector('[data-milaura-product-fragment] [data-milaura-recommendation-card]');
    return card ? document.importNode(card, true) : null;
  }

  async function fetchCatalogueFallbackCards(excludedIds, limit) {
    const excluded = new Set(excludedIds.map(String));
    const response = await fetch(
      `${storefrontRoot()}collections/all/products.json?limit=${PDP_CATALOGUE_SCAN_LIMIT}`,
      {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      }
    );
    if (!response.ok) return [];
    const payload = await response.json();
    const products = Array.isArray(payload.products) ? payload.products : [];
    const handles = products
      .filter((product) => {
        if (!product?.id || !product?.handle || excluded.has(String(product.id))) return false;
        if (!Array.isArray(product.images) || !product.images.length) return false;
        return Array.isArray(product.variants) && product.variants.some((variant) => variant.available);
      })
      .slice(0, Math.max(limit * 3, 9))
      .map((product) => product.handle);
    const resolved = await Promise.all(
      handles.map((handle) => fetchProductCard(handle).catch(function () { return null; }))
    );
    return uniqueByProductId(resolved.filter(Boolean), excludedIds, limit).map((card) => {
      card.dataset.recommendationOrigin = 'catalogue';
      card.dataset.fallbackTier = 'catalogue';
      return card;
    });
  }

  class MilauraRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.dataset.milauraInitialized === 'true') return;
      this.dataset.milauraInitialized = 'true';

      this.list = this.querySelector('[data-milaura-recommendation-list]');
      this.status = this.querySelector('[data-milaura-recommendation-status]');
      this.headingElement = this.querySelector('[data-milaura-recommendation-title]');
      this.subtitleElement = this.querySelector('[data-milaura-recommendation-subtitle]');
      this.defaultTitle = this.headingElement?.textContent.trim() || '';
      this.defaultSubtitle = this.subtitleElement?.textContent.trim() || '';
      this.parurePanel = this.querySelector('[data-milaura-parure]');
      this.cartProductIds = [];
      this.railControls = this.querySelector('[data-milaura-recommendation-controls]');
      this.railCount = this.querySelector('[data-milaura-rail-count]');
      this.railMeter = this.querySelector('[data-milaura-rail-meter]');
      this.railMeterValue = this.querySelector('[data-milaura-rail-meter-value]');
      this.context = this.dataset.context || 'editorial';
      this.limit = Number.parseInt(this.dataset.limit, 10) || 3;
      if (this.context === 'pdp') this.limit = PDP_CANDIDATE_LIMIT;
      this.minimum = Number.parseInt(this.dataset.minimum, 10) || 1;
      this.sourceProductIds = parseList(this.dataset.sourceProductIds);
      this.excludedProductIds = parseList(this.dataset.excludedProductIds);
      this.intents = parseList(this.dataset.intents);
      this.currentIntent = this.intents[0] || 'curated';
      this.loadGeneration = 0;
      this.initialCards = Array.from(this.querySelectorAll('[data-milaura-recommendation-card]'));
      this.fallbackCards = this.initialCards.map((card) => card.cloneNode(true));
      this.pdpCandidates = [];
      this.historyPromise = prepareHistory({
        id: this.dataset.currentProductId,
        handle: this.dataset.currentProductHandle,
      });

      this.bindInteractions();
      this.bindCartRefresh();
      this.bindDiagnosticResult();
      this.bindPdpVisibility();
      this.bindParure();

      if (this.dataset.state === 'ready' && this.context !== 'pdp') {
        this.renderCards(this.initialCards, this.currentIntent);
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.initialize();
        return;
      }

      this.loadObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.initialize();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.loadObserver.observe(this);
    }

    disconnectedCallback() {
      this.loadObserver?.disconnect();
      this.impressionObserver?.disconnect();
      if (this.cartRefreshHandler) document.removeEventListener('cart:updated', this.cartRefreshHandler);
      if (this.cartBusUnsubscriber) this.cartBusUnsubscriber();
      if (this.diagnosticHandler) window.removeEventListener('milaura:quiz-result', this.diagnosticHandler);
      this.pdpVisibilityObserver?.disconnect();
      this.teardownRibbon();
      if (this.context === 'pdp') document.documentElement.classList.remove('milaura-recommendations-in-view');
    }

    async initialize() {
      if (this.loading) return;
      this.loading = true;
      const loadGeneration = ++this.loadGeneration;
      const preservePdpFallback = this.context === 'pdp' && this.fallbackCards.length > 0;
      if (preservePdpFallback) this.setAttribute('aria-busy', 'true');
      else this.setState('loading');

      try {
        if (this.context === 'pdp') {
          await this.loadPdpRecommendations();
        } else if (this.context === 'cart-page' || this.context === 'cart-drawer') {
          await this.loadCartRecommendations();
        } else if (this.context === 'recent') {
          await this.loadRecentlyViewed(loadGeneration);
        } else if (this.context === 'diagnostic') {
          await this.loadDiagnosticRecommendations(null, loadGeneration);
        } else if (this.initialCards.length) {
          this.renderCards(this.initialCards, this.currentIntent);
        } else {
          this.setState('empty');
        }
      } catch (error) {
        if (loadGeneration !== this.loadGeneration) return;
        if (this.context === 'pdp' && this.renderPdpFallback()) {
          this.announce('Une sélection disponible est affichée.');
        } else {
          this.setState('error');
          this.announce('Les recommandations sont momentanément indisponibles.');
        }
      } finally {
        if (loadGeneration === this.loadGeneration) {
          this.loading = false;
          this.removeAttribute('aria-busy');
        }
      }
    }

    async loadPdpRecommendations() {
      const currentProductId = String(this.sourceProductIds[0] || '');
      if (!currentProductId) {
        if (!this.renderPdpFallback()) this.setState('empty');
        return;
      }

      const [cartProductIds, history] = await Promise.all([
        fetchCartProductIds().catch(function () { return []; }),
        this.historyPromise.catch(function () { return []; }),
      ]);
      this.cartProductIds = cartProductIds.map(String);
      const excluded = new Set([...this.excludedProductIds, currentProductId, ...cartProductIds].map(String));
      const [complementaryCards, relatedCards] = await Promise.all([
        fetchRecommendationCards(currentProductId, 'complementary', 10).catch(function () { return []; }),
        fetchRecommendationCards(currentProductId, 'related', 10).catch(function () { return []; }),
      ]);
      const fallbackCards = this.fallbackCards.map((card) => card.cloneNode(true));
      const recentIds = new Set(history.map((item) => String(item.id || '')));
      const source = {
        stone: this.dataset.currentProductStone || '',
        type: this.dataset.currentProductType || '',
        intention: this.dataset.currentProductIntention || '',
        family: this.dataset.currentProductFamily || '',
        finish: this.dataset.currentProductFinish || 'inconnu',
        audience: this.dataset.currentProductAudience || '',
        parure: this.dataset.currentProductParure || '',
        price: this.dataset.currentProductPrice || '',
        beadSize: this.dataset.currentProductBeadSize || '',
      };
      let candidatePool = [...complementaryCards, ...relatedCards, ...fallbackCards];
      let cards = rankPdpCards(candidatePool, source, recentIds, Array.from(excluded), this.limit);

      if (cards.length < this.limit) {
        const catalogueCards = await fetchCatalogueFallbackCards(
          [...excluded, ...cards.map((card) => String(card.dataset.productId || ''))],
          this.limit - cards.length
        ).catch(function () { return []; });
        candidatePool = [...candidatePool, ...catalogueCards];
        cards = rankPdpCards(candidatePool, source, recentIds, Array.from(excluded), this.limit);
      }

      if (cards.length) {
        this.renderCards(cards, 'adaptive');
        return;
      }

      if (!this.renderPdpFallback(Array.from(excluded))) this.setState('empty');
    }

    renderPdpFallback(excludedIds) {
      const excluded = excludedIds || this.excludedProductIds;
      const fallback = uniqueByProductId(
        this.fallbackCards.map((card) => card.cloneNode(true)),
        excluded,
        this.limit
      );
      if (!fallback.length) return false;
      fallback.forEach((card) => {
        card.dataset.recommendationGate = card.dataset.fallbackTier === 'same-collection' ? 'collection' : 'catalogue';
        card.dataset.recommendationSignal = card.dataset.recommendationGate;
        setPdpRecommendationReason(card, card.dataset.recommendationGate);
      });
      this.renderCards(fallback, 'adaptive-fallback');
      return true;
    }

    async loadCartRecommendations() {
      if (!this.sourceProductIds.length) {
        this.setState('empty');
        return;
      }

      const collected = [];
      const excluded = new Set(this.excludedProductIds.map(String));

      for (const sourceProductId of this.sourceProductIds) {
        const cards = await fetchRecommendationCards(sourceProductId, 'complementary', 4);
        cards.forEach((card) => {
          const productId = String(card.dataset.productId || '');
          if (!productId || excluded.has(productId)) return;
          excluded.add(productId);
          collected.push(card);
        });
        if (collected.length >= this.limit) break;
      }

      const cards = collected.slice(0, this.limit);
      if (!cards.length) {
        this.setState('empty');
        return;
      }
      this.renderCards(cards, 'complementary');
    }

    async loadRecentlyViewed(loadGeneration) {
      const history = await this.historyPromise;
      if (loadGeneration !== this.loadGeneration) return;
      const excluded = new Set(this.excludedProductIds.map(String));
      const candidates = history.filter((item) => !excluded.has(String(item.id))).slice(0, this.limit);
      if (!candidates.length) {
        this.setState('empty');
        return;
      }

      const resolved = await Promise.all(candidates.map((item) => fetchRecentCard(item.handle)));
      if (loadGeneration !== this.loadGeneration) return;
      const cards = uniqueByProductId(resolved.filter(Boolean), this.excludedProductIds, this.limit);
      if (!cards.length) {
        this.setState('empty');
        return;
      }
      this.renderCards(cards, 'recent');
    }

    async renderHandles(items, intent, loadGeneration) {
      const activeGeneration = loadGeneration || ++this.loadGeneration;
      if (activeGeneration !== this.loadGeneration) return;
      const validItems = Array.isArray(items) ? items.filter((item) => item?.handle) : [];
      if (!validItems.length) {
        this.setState('empty');
        return;
      }

      this.setState('loading');
      const resolved = await Promise.all(
        validItems.slice(0, this.limit).map(async (item) => ({
          item,
          card: await fetchProductCard(item.handle).catch(function () { return null; }),
        }))
      );
      if (activeGeneration !== this.loadGeneration) return;
      if (this.context === 'diagnostic' || this.context === 'account') {
        const privacy = await window.MilauraPreferenceStorage.getPreferenceState({ fresh: true });
        if (activeGeneration !== this.loadGeneration) return;
        if (!privacy.available || !privacy.allowed) {
          this.setState('empty');
          return;
        }
      }
      resolved.forEach(({ item, card }) => {
        const reasonElement = card?.querySelector('.milaura-recommendation-card__reason');
        if (item.reason && reasonElement) reasonElement.textContent = item.reason;
      });
      const cards = uniqueByProductId(
        resolved.map(({ card }) => card).filter(Boolean),
        this.excludedProductIds,
        this.limit
      );
      if (!cards.length) {
        this.setState('empty');
        return;
      }
      this.renderCards(cards, intent || 'curated');
    }

    async loadDiagnosticRecommendations(detail, loadGeneration) {
      const activeGeneration = loadGeneration || ++this.loadGeneration;
      let diagnostic = detail || this.currentDiagnostic || null;
      if (!diagnostic) {
        const privacy = await window.MilauraPreferenceStorage.getPreferenceState();
        if (activeGeneration !== this.loadGeneration) return;
        if (!privacy.available || !privacy.allowed) {
          this.setState('empty');
          return;
        }
        diagnostic = await window.MilauraPreferenceStorage.readDiagnostic();
        if (activeGeneration !== this.loadGeneration) return;
      }
      if (!diagnostic?.profileId && !diagnostic?.stoneHandle && !diagnostic?.stone) {
        this.setState('empty');
        return;
      }

      this.currentDiagnostic = diagnostic;
      this.setState('loading');
      const cartProductIds = await fetchCartProductIds().catch(function () { return []; });
      if (activeGeneration !== this.loadGeneration) return;
      const excludedIds = [...this.excludedProductIds, ...cartProductIds];
      const candidates = await resolveDiagnosticProductItems(diagnostic, excludedIds, this.limit);
      if (activeGeneration !== this.loadGeneration) return;
      if (!candidates.length) {
        this.setState('empty');
        return;
      }

      const resolved = await Promise.all(
        candidates.map(async (candidate) => ({
          candidate,
          card: await fetchProductCard(candidate.product.handle).catch(function () { return null; }),
        }))
      );
      if (activeGeneration !== this.loadGeneration) return;

      const seen = new Set();
      const records = resolved
        .filter(({ card }) => {
          const productId = String(card?.dataset.productId || '');
          if (!card || !productId || seen.has(productId)) return false;
          if (card.dataset.productAvailable === 'false' || card.dataset.hasGalleryImage === 'false') return false;
          seen.add(productId);
          return true;
        })
        .map(({ candidate, card }) => {
          card.dataset.recommendationSignal = candidate.signal;
          card.dataset.recommendationGate = candidate.signal;
          card.dataset.recommendationScore = String(candidate.score);
          return { card, productType: candidate.productType, signal: candidate.signal };
        });
      const selectedRecords = selectDiverseDiagnosticCards(records, this.limit);
      selectedRecords.forEach((record, index) => {
        const reasonElement = record.card.querySelector('.milaura-recommendation-card__reason');
        if (reasonElement) reasonElement.textContent = diagnosticProductReason(diagnostic, index, record.signal);
      });
      const selected = selectedRecords.map((record) => record.card);
      if (!selected.length) {
        this.setState('empty');
        return;
      }
      this.renderCards(selected, 'dynamic-diagnostic');
    }

    renderCards(cards, intent) {
      if (!this.list || !cards.length) {
        this.setState('empty');
        return;
      }

      const candidateCards = cards.slice(0, this.limit);
      const visibleCards = candidateCards;
      if (this.context === 'pdp') this.pdpCandidates = candidateCards;
      this.list.replaceChildren();
      visibleCards.forEach((card, index) => {
        card.classList.remove('milaura-recommendation-card--focal', 'milaura-recommendation-card--secondary');
        const focal = index === 0 && this.context !== 'recent';
        if (this.context === 'cart-page' || this.context === 'cart-drawer' || this.context === 'diagnostic' || !focal) {
          this.replaceMotionWithPoster(card);
        }
        card.classList.add(focal ? 'milaura-recommendation-card--focal' : 'milaura-recommendation-card--secondary');
        this.list.appendChild(card);
        this.prepareMotionMedia(card);
      });

      const renderedCount = this.list.children.length;
      this.currentIntent = intent;
      this.dataset.intent = intent;
      this.list.dataset.cardCount = String(renderedCount);
      this.list.dataset.candidateCount = String(candidateCards.length);
      this.configureRibbonComposition();
      this.updateCopy(intent);
      this.setupParure();
      this.setState('ready');
      this.announce(`${renderedCount} ${renderedCount > 1 ? 'produits proposés' : 'produit proposé'}.`);
      this.observeImpression();
      document.dispatchEvent(new CustomEvent('milaura:recommendations:loaded', { detail: { root: this } }));
    }

    configureRibbonComposition() {
      this.teardownRibbon();
      const cards = Array.from(this.list?.querySelectorAll('[data-milaura-recommendation-card]') || []);
      const ribbon = (this.context === 'pdp' || this.context === 'diagnostic') && cards.length > 0;
      const hasMultipleCards = cards.length > 1;

      this.dataset.layout = ribbon ? 'ribbon' : 'gallery';
      this.dataset.ribbonRevealed = 'false';
      if (this.railControls) this.railControls.hidden = !ribbon || !hasMultipleCards;
      if (this.railMeter) this.railMeter.hidden = !ribbon || !hasMultipleCards;
      cards.forEach((card, index) => {
        card.style.setProperty('--milaura-reco-order', String(index));
        card.dataset.active = 'false';
        card.dataset.ribbonPosition = String(index + 1);
      });

      this.ribbonCards = cards;
      if (!cards.length) return;
      this.activateRibbonCard(cards[0]);
      if (!ribbon) {
        this.dataset.ribbonRevealed = 'true';
        return;
      }

      this.ribbonScrollHandler = () => {
        if (this.ribbonFrame) return;
        this.ribbonFrame = window.requestAnimationFrame(() => {
          this.ribbonFrame = null;
          this.syncActiveRibbonCard();
        });
      };
      this.list.addEventListener('scroll', this.ribbonScrollHandler, { passive: true });
      window.requestAnimationFrame(() => {
        if (this.isConnected) this.dataset.ribbonRevealed = 'true';
      });
    }

    activateRibbonCard(card) {
      if (!card || !this.list?.contains(card) || !this.ribbonCards?.includes(card)) return;
      this.ribbonCards.forEach((candidate) => {
        candidate.dataset.active = String(candidate === card);
      });
      this.updateRibbonPosition(this.ribbonCards.indexOf(card));
    }

    teardownRibbon() {
      if (this.ribbonFrame) window.cancelAnimationFrame(this.ribbonFrame);
      this.ribbonFrame = null;
      if (this.ribbonScrollHandler && this.list) this.list.removeEventListener('scroll', this.ribbonScrollHandler);
      this.ribbonScrollHandler = null;
      this.ribbonCards = [];
    }

    syncActiveRibbonCard() {
      if (this.dataset.layout !== 'ribbon' || !this.list || !this.ribbonCards.length) return;
      const listRect = this.list.getBoundingClientRect();
      const center = listRect.left + listRect.width / 2;
      const closest = this.ribbonCards.reduce((current, card) => {
        const rect = card.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        return !current || distance < current.distance ? { card, distance } : current;
      }, null);
      if (closest?.card) this.activateRibbonCard(closest.card);
    }

    updateRibbonPosition(index) {
      if (index < 0 || !this.ribbonCards.length) return;
      const total = this.ribbonCards.length;
      if (this.railCount) {
        this.railCount.textContent = `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
      }
      if (this.railMeterValue) this.railMeterValue.style.width = `${((index + 1) / total) * 100}%`;
      const previous = this.querySelector('[data-milaura-rail-control="previous"]');
      const next = this.querySelector('[data-milaura-rail-control="next"]');
      if (previous) previous.disabled = index === 0;
      if (next) next.disabled = index === total - 1;
    }

    moveRibbon(direction) {
      if (!this.ribbonCards.length) return;
      const active = this.ribbonCards.findIndex((card) => card.dataset.active === 'true');
      const nextIndex = Math.min(this.ribbonCards.length - 1, Math.max(0, active + direction));
      const nextCard = this.ribbonCards[nextIndex];
      if (!nextCard) return;
      this.activateRibbonCard(nextCard);
      this.centerRibbonCard(nextCard);
      const title = nextCard.querySelector('.grid__card-title')?.textContent?.trim();
      if (title) this.announce(`${nextIndex + 1} sur ${this.ribbonCards.length} : ${title}.`);
    }

    centerRibbonCard(card) {
      if (!card || !this.list) return;
      const left = card.offsetLeft - (this.list.clientWidth - card.offsetWidth) / 2;
      this.list.scrollTo({
        left: Math.max(0, left),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    setupParure() {
      if (this.context !== 'pdp' || !this.parurePanel || !this.list) return;
      const cards = Array.from(this.list.querySelectorAll('[data-milaura-recommendation-card]'));
      const active = cards.some((card) => card.dataset.parureMember === 'true');
      this.dataset.parure = String(active);
      this.parurePanel.hidden = !active;
      if (this.headingElement) {
        this.headingElement.textContent = active && this.dataset.parureTitle ? this.dataset.parureTitle : this.defaultTitle;
      }
      if (this.subtitleElement) {
        this.subtitleElement.textContent =
          active && this.dataset.parureSubtitle ? this.dataset.parureSubtitle : this.defaultSubtitle;
      }
      if (!active) return;

      cards.forEach((card) => {
        if (card.dataset.parureMember !== 'true') return;
        if (card.querySelector('[data-milaura-parure-check]')) return;
        if (card.dataset.productAvailable === 'false' || !card.dataset.variantId) return;
        if (card.querySelector('.milaura-editorial-purchase__choose')) return;
        const name =
          card.querySelector('.grid__card-title, h3')?.textContent.replace(/\s+/g, ' ').trim() ||
          'cette pièce';
        const label = document.createElement('label');
        label.className = 'milaura-parure__check';
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.setAttribute('data-milaura-parure-check', '');
        input.setAttribute('aria-label', `Ajouter ${name} à la sélection`);
        label.appendChild(input);
        card.dataset.parureCheckable = 'true';
        card.prepend(label);
      });
      this.updateParureSummary();
    }

    parureSelection() {
      const cards = Array.from(this.list?.querySelectorAll('[data-milaura-recommendation-card]') || []);
      const checked = cards.filter((card) => card.querySelector('[data-milaura-parure-check]')?.checked);
      const sourceId = String(this.sourceProductIds[0] || '');
      const sourceInCart = this.cartProductIds.map(String).includes(sourceId);
      return { checked, sourceInCart };
    }

    updateParureSummary() {
      if (!this.parurePanel || this.parurePanel.hidden) return;
      const { checked, sourceInCart } = this.parureSelection();
      const pieces = checked.length;
      const plural = pieces > 1 ? 's' : '';
      const sourcePrice = sourceInCart ? 0 : Number(this.dataset.currentProductPrice) || 0;
      const total = checked.reduce((sum, card) => sum + (Number(card.dataset.productPrice) || 0), sourcePrice);
      const itemCount = pieces + (sourceInCart ? 0 : 1);
      const count = this.parurePanel.querySelector('[data-milaura-parure-count]');
      const totalElement = this.parurePanel.querySelector('[data-milaura-parure-total]');
      const button = this.parurePanel.querySelector('[data-milaura-parure-add]');
      if (count) {
        count.textContent = sourceInCart
          ? `${pieces} pièce${plural} sélectionnée${plural}`
          : `${this.dataset.parureSourceLabel || 'Votre bijou'} + ${pieces} pièce${plural}`;
      }
      if (totalElement) totalElement.textContent = formatParurePrice(total);
      if (button && button.getAttribute('aria-busy') !== 'true') {
        button.textContent = itemCount > 1 ? `Ajouter les ${itemCount} pièces au panier` : 'Ajouter au panier';
      }
    }

    bindParure() {
      if (!this.parurePanel) return;
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-milaura-parure-check]')) return;
        const status = this.parurePanel.querySelector('[data-milaura-parure-status]');
        if (status) status.textContent = '';
        this.updateParureSummary();
      });
      this.parurePanel.querySelector('[data-milaura-parure-add]')?.addEventListener('click', (event) => {
        this.addParure(event.currentTarget);
      });
    }

    async addParure(button) {
      if (button.getAttribute('aria-busy') === 'true') return;
      const status = this.parurePanel.querySelector('[data-milaura-parure-status]');
      const { checked, sourceInCart } = this.parureSelection();
      const items = [];
      const productForm = document.querySelector('product-form form[action*="/cart/add"]');
      const sourceVariant = Number.parseInt(productForm?.querySelector('[name="id"]')?.value || '', 10);
      const sourceQuantity = Number.parseInt(productForm?.querySelector('[name="quantity"]')?.value || '1', 10) || 1;
      if (!sourceInCart && sourceVariant) items.push({ id: sourceVariant, quantity: sourceQuantity });
      checked.forEach((card) => {
        const variantId = Number.parseInt(card.dataset.variantId || '', 10);
        if (variantId) items.push({ id: variantId, quantity: 1 });
      });
      if (!items.length) {
        if (status) status.textContent = 'Cochez au moins une pièce.';
        return;
      }

      const drawer = document.querySelector('cart-drawer');
      const cartAddRoute = window.routes?.cart_add_url || `${storefrontRoot()}cart/add`;
      const cartAddUrl = cartAddRoute.endsWith('.js') ? cartAddRoute : `${cartAddRoute}.js`;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Ajout…';
      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({
            items,
            sections: drawer?.getSectionsToRender?.().map((section) => section.id) || [],
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.status) throw new Error(data?.description || `cart_add_${response.status}`);

        if (status) status.textContent = 'Ajouté au panier';
        if (typeof window.milauraCartToast === 'function') window.milauraCartToast('Ajouté au panier');
        checked.forEach((card) => {
          document.dispatchEvent(
            new CustomEvent('milaura:recommendation-added', {
              detail: {
                context: 'pdp-parure',
                intent: this.dataset.intent || '',
                productId: card.dataset.productId || '',
                variantId: card.dataset.variantId || '',
                quantity: 1,
              },
            })
          );
        });
        try {
          drawer?.renderContents?.(data);
        } catch (error) {
          console.error('MilAura parure drawer refresh failed:', error);
        }
        const cartRoute = window.routes?.cart_url || `${storefrontRoot()}cart`;
        const cartResponse = await fetch(cartRoute.endsWith('.js') ? cartRoute : `${cartRoute}.js`, {
          credentials: 'same-origin',
        });
        if (cartResponse.ok) {
          const cart = await cartResponse.json();
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
          window.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        }
      } catch (error) {
        console.error('MilAura parure add failed:', error);
        if (status) status.textContent = "L'ajout n'a pas abouti. Réessayez.";
      } finally {
        button.removeAttribute('aria-busy');
        this.updateParureSummary();
      }
    }

    updateCopy(intent) {
      if (intent === 'complementary') {
        if (this.dataset.complementaryTitle && this.headingElement) {
          this.headingElement.textContent = this.dataset.complementaryTitle;
        }
        if (this.dataset.complementarySubtitle && this.subtitleElement) {
          this.subtitleElement.textContent = this.dataset.complementarySubtitle;
        }
      }
      if (intent === 'related') {
        if (this.dataset.relatedTitle && this.headingElement) this.headingElement.textContent = this.dataset.relatedTitle;
        if (this.dataset.relatedSubtitle && this.subtitleElement) {
          this.subtitleElement.textContent = this.dataset.relatedSubtitle;
        }
      }
    }

    setState(state) {
      this.dataset.state = state;
      this.syncPdpVisibility();
    }

    announce(message) {
      if (this.status) this.status.textContent = message;
    }

    bindInteractions() {
      this.addEventListener('pointerover', (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        const card = event.target.closest('[data-milaura-recommendation-card]');
        if (!card) return;
        this.activateRibbonCard(card);
      });

      this.addEventListener('focusin', (event) => {
        const card = event.target.closest('[data-milaura-recommendation-card]');
        if (!card) return;
        this.activateRibbonCard(card);
      });

      this.addEventListener('click', (event) => {
        const railControl = event.target.closest('[data-milaura-rail-control]');
        if (railControl) {
          event.preventDefault();
          this.moveRibbon(railControl.dataset.milauraRailControl === 'next' ? 1 : -1);
          return;
        }

        const card = event.target.closest('[data-milaura-recommendation-card]');
        if (!card) return;
        const link = event.target.closest('.grid__card');
        if (!link) return;
        publishAnalytics('milaura:recommendation_click', {
          context: this.context,
          intent: this.currentIntent,
          productId: card.dataset.productId,
          signal: card.dataset.recommendationSignal || '',
          score: Number(card.dataset.recommendationScore) || 0,
          position: Number(card.dataset.ribbonPosition) || Array.from(this.list?.children || []).indexOf(card) + 1,
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (event.target.closest('button, input, select, textarea')) return;
        if (!this.list) return;
        const direction = event.key === 'ArrowRight' ? 1 : -1;

        if (this.dataset.layout === 'ribbon') {
          event.preventDefault();
          this.moveRibbon(direction);
          return;
        }

        if (this.list.scrollWidth <= this.list.clientWidth) return;
        const card = this.list.querySelector('[data-milaura-recommendation-card]');
        if (!card) return;
        event.preventDefault();
        const styles = window.getComputedStyle(this.list);
        const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollBy({
          left: direction * (card.getBoundingClientRect().width + gap),
          behavior: reduceMotion ? 'auto' : 'smooth',
        });
      });
    }

    prepareMotionMedia(card) {
      if (!card.classList.contains('milaura-recommendation-card--focal')) return;
      const video = card.querySelector('.milaura-recommendation-card__video');
      if (!video || video.dataset.milauraMotionReady === 'true') return;
      video.dataset.milauraMotionReady = 'true';
      video.muted = true;

      if (
        window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        Boolean(window.navigator.connection?.saveData)
      ) {
        this.replaceMotionWithPoster(card);
        return;
      }

      const play = () => {
        const promise = video.play();
        if (promise && typeof promise.catch === 'function') promise.catch(function () {});
      };
      const pause = () => video.pause();

      card.addEventListener('pointerenter', play);
      card.addEventListener('pointerleave', pause);
      card.addEventListener('focusin', play);
      card.addEventListener('focusout', (event) => {
        if (!card.contains(event.relatedTarget)) pause();
      });
    }

    replaceMotionWithPoster(card) {
      const video = card.querySelector('.milaura-recommendation-card__video');
      if (!video) return;
      const fallback = video.querySelector('img');
      const image = fallback ? fallback.cloneNode(true) : document.createElement('img');
      const poster = video.getAttribute('poster');
      if (!image.getAttribute('src') && poster) image.setAttribute('src', poster);
      image.className = 'grid__card-image grid__card-image--primary';
      image.alt = card.querySelector('.grid__card-title')?.textContent?.trim() || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      video.replaceWith(image);
    }

    refreshCartItems(items) {
      if (!Array.isArray(items)) return;
      const productIds = items.map((item) => String(item.product_id || item.productId || '')).filter(Boolean);
      window.clearTimeout(this.cartRefreshTimer);

      if (!productIds.length) {
        this.sourceProductIds = [];
        this.excludedProductIds = [];
        this.list?.replaceChildren();
        this.setState('empty');
        return;
      }

      this.cartRefreshTimer = window.setTimeout(() => {
        this.sourceProductIds = productIds;
        this.excludedProductIds = productIds;
        this.list?.replaceChildren();
        this.initialCards = [];
        this.dataset.milauraImpressionSent = '';
        this.initialize();
      }, 80);
    }

    refreshPdpCartItems(items) {
      if (!Array.isArray(items)) return;
      const currentProductId = String(this.sourceProductIds[0] || '');
      const cartProductIds = items
        .map((item) => String(item.product_id || item.productId || ''))
        .filter(Boolean);
      this.cartProductIds = cartProductIds;
      const excluded = new Set([currentProductId, ...cartProductIds]);
      const remaining = this.pdpCandidates.filter((card) => {
        const productId = String(card.dataset.productId || '');
        return productId && !excluded.has(productId) && card.dataset.productAvailable !== 'false';
      });

      window.clearTimeout(this.cartRefreshTimer);
      this.cartRefreshTimer = window.setTimeout(() => {
        this.excludedProductIds = Array.from(excluded);
        this.dataset.milauraImpressionSent = '';
        if (remaining.length) {
          this.renderCards(remaining, 'adaptive');
          return;
        }
        this.loading = false;
        this.loadPdpRecommendations().catch(() => {
          if (!this.renderPdpFallback(Array.from(excluded))) this.setState('error');
        });
      }, 80);
    }

    bindCartRefresh() {
      if (this.context !== 'cart-page' && this.context !== 'pdp') return;
      this.cartRefreshHandler = (event) => {
        if (this.context === 'pdp') this.refreshPdpCartItems(event.detail?.items);
        else this.refreshCartItems(event.detail?.items);
      };
      document.addEventListener('cart:updated', this.cartRefreshHandler);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartBusUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (this.context === 'pdp') this.refreshPdpCartItems(event.cartData?.items);
          else this.refreshCartItems(event.cartData?.items);
        });
      }
    }

    bindDiagnosticResult() {
      if (this.context !== 'diagnostic') return;
      this.diagnosticHandler = (event) => {
        const loadGeneration = ++this.loadGeneration;
        this.loading = false;
        this.loadDiagnosticRecommendations(event.detail || null, loadGeneration).catch(() => {
          if (loadGeneration !== this.loadGeneration) return;
          this.setState('error');
          this.announce('Les recommandations sont momentanément indisponibles.');
        });
      };
      window.addEventListener('milaura:quiz-result', this.diagnosticHandler);
    }

    bindPdpVisibility() {
      if (this.context !== 'pdp' || !('IntersectionObserver' in window)) return;
      this.pdpVisibilityObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.pdpIsIntersecting = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.08);
          this.syncPdpVisibility();
        },
        { threshold: [0, 0.08] }
      );
      this.pdpVisibilityObserver.observe(this);
    }

    syncPdpVisibility() {
      if (this.context !== 'pdp') return;
      document.documentElement.classList.toggle(
        'milaura-recommendations-in-view',
        Boolean(this.pdpIsIntersecting && this.dataset.state === 'ready')
      );
    }

    observeImpression() {
      if (this.dataset.milauraImpressionSent === 'true') return;
      if (!('IntersectionObserver' in window)) {
        this.publishImpression();
        return;
      }
      this.impressionObserver?.disconnect();
      this.impressionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          this.publishImpression();
        },
        { threshold: 0.35 }
      );
      this.impressionObserver.observe(this);
    }

    publishImpression() {
      if (this.dataset.milauraImpressionSent === 'true') return;
      this.dataset.milauraImpressionSent = 'true';
      publishAnalytics('milaura:recommendation_impression', {
        context: this.context,
        intent: this.currentIntent,
        productIds: Array.from(this.list?.querySelectorAll('[data-milaura-recommendation-card]') || []).map(
          (card) => card.dataset.productId
        ),
        products: Array.from(this.list?.querySelectorAll('[data-milaura-recommendation-card]') || []).map((card) => ({
          productId: card.dataset.productId,
          signal: card.dataset.recommendationSignal || '',
          score: Number(card.dataset.recommendationScore) || 0,
        })),
      });
    }
  }

  if (!customElements.get('milaura-recommendations')) {
    customElements.define('milaura-recommendations', MilauraRecommendations);
  }

  window.MilauraRecommendations = Object.freeze({
    getPreferenceState: window.MilauraPreferenceStorage.getPreferenceState,
    diagnosticIntentHandles,
    rankDiagnosticMatches,
    rankDiagnosticProducts,
    diagnosticProductReason,
    selectDiverseDiagnosticCards,
    rankPdpCards,
  });
  document.dispatchEvent(new CustomEvent('milaura:recommendations:ready'));

  document.addEventListener('milaura:recommendation-added', function (event) {
    publishAnalytics('milaura:recommendation_add', event.detail || {});
  });

  document.addEventListener('milaura:preferences-changed', function (event) {
    window.milauraRecommendationHistoryPromise = null;

    const preferencesExplicitlyDenied = Boolean(event.detail?.available && !event.detail.allowed);
    if (preferencesExplicitlyDenied) {
      try {
        window.localStorage.removeItem(HISTORY_KEY);
      } catch (error) {}
    }

    document
      .querySelectorAll(
        'milaura-recommendations[data-context="recent"], milaura-recommendations[data-context="diagnostic"], milaura-recommendations[data-context="account"]'
      )
      .forEach((recommendations) => {
        recommendations.loadGeneration += 1;
        if (!event.detail || !event.detail.allowed) {
          recommendations.list?.replaceChildren();
          recommendations.setState('empty');
        }
        recommendations.historyPromise = prepareHistory({
          id: recommendations.dataset.currentProductId,
          handle: recommendations.dataset.currentProductHandle,
        });
        recommendations.loading = false;
        recommendations.initialize();
      });
  });
})();
